import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'components/ui/button';
import { Form, Row, Col } from 'react-bootstrap-5';
import { isEmpty, map, omitBy, uniq } from 'lodash';

import { post, get, put } from 'helpers/api';
import { requestErrors } from 'helpers/api_errors';
import { countries, isEuCountry, getUnitedStatesId } from 'helpers/countries';
import PhoneNumberInput from 'components/ui/phone_number_input';
import MarketingPreferences from '../marketing_preferences';

import PasswordInput from 'components/form/password_input';
import LoadingSpinner from 'components/ui/loading_spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { getSourceParam, investorTypeMap } from '../../helpers/vault_access_helper';
import ErrorsForInput, {
  errorStringsFor, getErrorsForInput, OtherErrors, PROFILE_FIELD_ERROR_KEYS, requestErrorMessage
} from './errors_for_input';
import { getQueryParameter } from 'helpers/browser_helpers';
import { PROFILE_OPTIONS, setProfileData } from '../../helpers/preview';
import { fetchTranslation } from '../../../../helpers/i18n';
import { getCountryFields } from '../../helpers/country_fields';
import { getPasswordInputTranslations } from '../../helpers/input_helpers';

export default function AccountForm({
  accountPath,
  countries: countriesProp,
  invitedLeadAccountPath,
  isUpdate,
  readonly,
  sharingId,
  onCancel,
  onNext
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [countryId, setCountryId] = useState(1);
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [primaryRole, setPrimaryRole] = useState();
  const [investorTypes, setInvestorTypes] = useState([]);
  const [errors, setErrors] = useState([]);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [marketingPreferences, setMarketingPreferences] = useState([]);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // This is only needed on create so treat updates as if they already agreed
  const [agreeToTerms, setAgreeToTerms] = useState(isUpdate);

  const isEuCountrySelected = isEuCountry(countryId);
  const isUsSelected = countryId === getUnitedStatesId();

  const { countryList, stateLabel, zipLabel, stateIsEnum, stateOptions, showZip } =
    getCountryFields(countriesProp, countryId);

  React.useEffect(() => {
    let promise = null;
    if (getQueryParameter('email') && invitedLeadAccountPath) {
      promise = getInvitedLeadData();
    } else if (isUpdate) {
      promise = getProfileData();
    }

    if (promise === null) return;

    setFetchingDetails(true);
    promise.finally(() => setFetchingDetails(false));
  }, []);

  function getProfileData() {
    return get(accountPath).then(({ profile }) => {
      setFirstName(profile.firstName || '');
      setLastName(profile.lastName || '');
      setPhoneNumber(profile.phoneNumber || '');
      setCompanyName(profile.companyName || '');
      setJobTitle(profile.jobTitle || '');
      setPrimaryRole(profile.primaryRole);
      setInvestorTypes(profile.investorTypes || []);
      if (profile.countryId) setCountryId(profile.countryId);
    });
  }

  function getInvitedLeadData() {
    return get(invitedLeadAccountPath).then(({ profileFields }) => {
      setEmail(profileFields.email || '');
      setFirstName(profileFields.firstName || '');
      setLastName(profileFields.lastName || '');
      setPhoneNumber(profileFields.phoneNumber || '');
      setCompanyName(profileFields.companyName || '');
      setJobTitle(profileFields.jobTitle || '');
      if (profileFields.countryId) setCountryId(profileFields.countryId);
    });
  }

  const createAccount = (e) => {
    e.preventDefault();
    setFormSubmitted(true);

    // Validate marketing preferences for EU countries when creating account
    if (!isUpdate && isEuCountrySelected && marketingPreferences.length === 0) {
      return;
    }

    setSubmittingForm(true);

    const loginCredential = omitBy({
      email,
      password
    }, isEmpty);

    let profileAttributes = {
      firstName,
      lastName,
      phoneNumber,
      companyName,
      jobTitle,
      primaryRole,
      investorTypes,
      fromConnect: true
    };

    // Address inputs are only present when creating a new profile
    if (!isUpdate) {
      profileAttributes = {
        ...profileAttributes,
        countryId,
        address,
        city,
        state,
        zip
      };
    }

    const payload = {
      sharingId: sharingId,
      readonly,
      loginCredential: {
        ...loginCredential,
        profileAttributes
      },
      source: getSourceParam(),
      marketingPreferences
    };

    if (!isUpdate && getQueryParameter('email')) {
      payload.invitedLead = {
        email: getQueryParameter('email'),
        token: getQueryParameter('lead_token')
      };
    }

    const requestMethod = isUpdate ? put : post;

    requestMethod(accountPath, payload)
      .then(() => {
        if (readonly) {
          const updatedProfile = {
            [PROFILE_OPTIONS.ATTRIBUTES]: {
              ...profileAttributes,
              login_credential_attributes: loginCredential
            },
            [PROFILE_OPTIONS.PROFILE_PRESENT]: true
          };

          setProfileData(updatedProfile);
        }

        onNext();
      })
      .catch((errors) => {
        const failed = isUpdate ? requestErrorMessage() : fetchTranslation('account_creation_error');
        setErrors(requestErrors(errors, failed));
      })
      .finally(() => setSubmittingForm(false));
  };

  const setInvestorTypesFromInput = (e) => {
    if (e.target.checked) {
      setInvestorTypes([...investorTypes, e.target.value]);
    } else {
      setInvestorTypes(investorTypes.filter(typeKey => typeKey !== e.target.value));
    }
  };

  const uniqErrors = uniq(errors);
  // Only the inputs on screen can claim a message: the update form asks for neither email nor password.
  const claimedErrorKeys = [
    ...PROFILE_FIELD_ERROR_KEYS,
    'primary_role_in_errors',
    ...(isUpdate ? [] : ['email_in_errors', 'password_in_errors'])
  ];

  function renderFormFields() {
    return (
      <React.Fragment>
        {!isUpdate && (
          <React.Fragment>
            <Form.Group>
              <Form.Label>{fetchTranslation('email_address')} <span className="text-danger">*</span></Form.Label>
              <Form.Control
                autoFocus
                isInvalid={getErrorsForInput(uniqErrors, fetchTranslation('email_in_errors')).length > 0}
                name="email"
                placeholder={fetchTranslation('enter_email_address')}
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
              <ErrorsForInput errors={uniqErrors} errorString={fetchTranslation('email_in_errors')}/>
            </Form.Group>
            <div>
              <PasswordInput
                inputProps={{
                  isInvalid: getErrorsForInput(uniqErrors, fetchTranslation('password_in_errors')).length > 0
                }}
                value={password}
                onChange={setPassword}
                {...getPasswordInputTranslations()}
              />
              <ErrorsForInput errors={uniqErrors} errorString={fetchTranslation('password_in_errors')}/>
              <div className="mt-3">
                <small>
                  {fetchTranslation('buildout_password_info')}
                </small>
              </div>
            </div>
          </React.Fragment>
        )}
        <Form.Group>
          <Form.Label>{fetchTranslation('role')} <span className="text-danger">*</span></Form.Label>
          <div>
            <Form.Check
              checked={primaryRole === 'investor_owner'}
              id="user_type-0"
              isInvalid={getErrorsForInput(uniqErrors, fetchTranslation('primary_role_in_errors')).length > 0}
              label={fetchTranslation('investor_owner_developer')}
              name="user_type_investor"
              type="radio"
              value="investor_owner"
              onChange={e => setPrimaryRole(e.target.value)}
            />
            <Form.Check
              checked={primaryRole === 'commercial_broker'}
              id="user_type-1"
              isInvalid={getErrorsForInput(uniqErrors, fetchTranslation('primary_role_in_errors')).length > 0}
              label={fetchTranslation('commercial_broker')}
              name="user_type_commercial_broker"
              type="radio"
              value="commercial_broker"
              onChange={e => setPrimaryRole(e.target.value)}
            />
            <Form.Check
              checked={primaryRole === 'residential_broker'}
              id="user_type-2"
              isInvalid={getErrorsForInput(uniqErrors, fetchTranslation('primary_role_in_errors')).length > 0}
              label={fetchTranslation('residential_broker')}
              name="user_type_residential_broker"
              type="radio"
              value="residential_broker"
              onChange={e => setPrimaryRole(e.target.value)}
            />
            <Form.Check
              checked={primaryRole === 'other'}
              id="user_type-3"
              isInvalid={getErrorsForInput(uniqErrors, fetchTranslation('primary_role_in_errors')).length > 0}
              label={fetchTranslation('other_service_provider')}
              name="user_type_other"
              type="radio"
              value="other"
              onChange={e => setPrimaryRole(e.target.value)}
            />
            <ErrorsForInput errors={uniqErrors} errorString={fetchTranslation('primary_role_in_errors')}/>
          </div>
        </Form.Group>
        {primaryRole === 'investor_owner' && (
          <Form.Group>
            <Form.Label>{fetchTranslation('investor_type')} </Form.Label>
            {Object.keys(investorTypeMap).map(key => (
              <Form.Check
                checked={investorTypes.includes(key)}
                id={`investory-type-${key}`}
                key={key}
                label={fetchTranslation(key)}
                name={`investor_type_${key}`}
                type="checkbox"
                value={key}
                onChange={setInvestorTypesFromInput}
              />
            ))}
          </Form.Group>
        )}

        <Form.Group>
          <Form.Label>{fetchTranslation('first_name')} <span className="text-danger">*</span> </Form.Label>
          <Form.Control
            isInvalid={getErrorsForInput(uniqErrors, fetchTranslation('first_name_in_errors')).length > 0}
            name="first_name"
            placeholder={fetchTranslation('first_name')}
            required
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
          />
          <ErrorsForInput errors={uniqErrors} errorString={fetchTranslation('first_name_in_errors')}/>
        </Form.Group>
        <Form.Group>
          <Form.Label>{fetchTranslation('last_name')} <span className="text-danger">*</span></Form.Label>
          <Form.Control
            isInvalid={getErrorsForInput(uniqErrors, fetchTranslation('last_name_in_errors')).length > 0}
            name="last_name"
            placeholder={fetchTranslation('last_name')}
            required
            value={lastName}
            onChange={e => setLastName(e.target.value)}
          />
          <ErrorsForInput errors={uniqErrors} errorString={fetchTranslation('last_name_in_errors')}/>
        </Form.Group>
        <Form.Group>
          <Form.Label>{fetchTranslation('phone_number')} <span className="text-danger">*</span> </Form.Label>
          <PhoneNumberInput
            className={
              getErrorsForInput(uniqErrors, fetchTranslation('phone_number_in_errors')).length > 0 ? 'is-invalid' : ''
            }
            defaultCountry="US"
            name="phone_number"
            placeholder={fetchTranslation('phone_number_placeholder')}
            value={phoneNumber}
            onChange={setPhoneNumber}
          />
          <ErrorsForInput errors={uniqErrors} errorString={fetchTranslation('phone_number_in_errors')}/>
        </Form.Group>
        <Form.Group>
          <Form.Label>{fetchTranslation('company')} <span className="text-danger">*</span></Form.Label>
          <Form.Control
            isInvalid={getErrorsForInput(uniqErrors, fetchTranslation('company_in_errors')).length > 0}
            name="company"
            placeholder={fetchTranslation('company')}
            required
            value={companyName}
            onChange={e => setCompanyName(e.target.value)}
          />
          <ErrorsForInput errors={uniqErrors} errorString={fetchTranslation('company_in_errors')}/>
        </Form.Group>
        <Form.Group>
          <Form.Label>{fetchTranslation('job_title')} <span className="text-danger">*</span></Form.Label>
          <Form.Control
            isInvalid={getErrorsForInput(uniqErrors, fetchTranslation('job_title_in_errors')).length > 0}
            name="job_title"
            placeholder={fetchTranslation('job_title')}
            required
            value={jobTitle}
            onChange={e => setJobTitle(e.target.value)}
          />
          <ErrorsForInput errors={uniqErrors} errorString={fetchTranslation('job_title_in_errors')}/>
        </Form.Group>
        {!isUpdate && (
          <React.Fragment>
            <Form.Group>
              <Form.Label>{fetchTranslation('country')} <span className="text-danger">*</span></Form.Label>
              <Form.Select
                name="country"
                value={countryId}
                onChange={e => setCountryId(Number(e.target.value))}
              >
                {countryList
                  ? countryList.map(c => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))
                  : map(countries, ({ id, label }) => (
                    <option key={id} value={id}>{label}</option>
                  ))}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>{fetchTranslation('address')}</Form.Label>
              <Form.Control
                name="address"
                placeholder={fetchTranslation('address')}
                value={address}
                onChange={e => setAddress(e.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{fetchTranslation('city')}</Form.Label>
              <Form.Control
                name="city"
                placeholder={fetchTranslation('city')}
                value={city}
                onChange={e => setCity(e.target.value)}
              />
            </Form.Group>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>{stateLabel}</Form.Label>
                  {stateIsEnum ? (
                    <Form.Select
                      name="state"
                      value={state}
                      onChange={e => setState(e.target.value)}
                    >
                      <option value=""/>
                      {stateOptions.map(([label, value]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      name="state"
                      placeholder={stateLabel}
                      value={state}
                      onChange={e => setState(e.target.value)}
                    />
                  )}
                </Form.Group>
              </Col>
              {showZip && (
                <Col>
                  <Form.Group>
                    <Form.Label>{zipLabel}</Form.Label>
                    <Form.Control
                      name="zip"
                      placeholder={zipLabel}
                      value={zip}
                      onChange={e => setZip(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>
          </React.Fragment>
        )}
        {!isUpdate && !isUsSelected && (
          <MarketingPreferences
            required={isEuCountrySelected}
            showError={formSubmitted}
            value={marketingPreferences}
            onChange={setMarketingPreferences}
          />
        )}
        {!isUpdate && (
          <Form.Group>
            <Form.Check
              checked={agreeToTerms}
              id="terms-agreement"
              label={
                <span>
                  {fetchTranslation('agree_to_terms')}&nbsp;
                  <a href="https://www.buildout.com/terms-of-use">
                    <strong>
                      {fetchTranslation('buildout_terms_of_service')}
                    </strong>
                  </a>
                </span>
              }
              onChange={e => setAgreeToTerms(e.target.checked)}
            />
          </Form.Group>
        )}

        <OtherErrors errors={uniqErrors} errorStrings={errorStringsFor(claimedErrorKeys)}/>
        <Button disabled={!agreeToTerms || submittingForm} name="save" type="submit">
          {isUpdate ? fetchTranslation('get_access') : fetchTranslation('create_account')}
        </Button>
      </React.Fragment>
    );
  }

  return (
    <form className="d-flex flex-column gap-4" noValidate onSubmit={createAccount}>
      {typeof onCancel === 'function' && (
        <div>
          <Button className="d-none d-lg-block" type="button" variant="ghost" onClick={onCancel}>
            <FontAwesomeIcon className="me-2" icon={faChevronLeft} size="lg"/>
            {fetchTranslation('back')}
          </Button>
        </div>
      )}
      <h5 className="text-center m-0">
        {isUpdate
          ? fetchTranslation('we_just_need_a_few_details')
          : <strong>{fetchTranslation('create_an_account')}</strong>}
      </h5>
      <p className="text-center m-0">
        {isUpdate ?
          fetchTranslation('additional_info_to_access') :
          fetchTranslation('create_buildout_account_to_access')
        }
      </p>

      {fetchingDetails ? (
        <div className="text-center">
          <LoadingSpinner />
        </div>
      ) : (
        renderFormFields()
      )}
    </form>
  );
}

AccountForm.propTypes = {
  accountPath: PropTypes.string.isRequired,
  countries: PropTypes.array,
  invitedLeadAccountPath: PropTypes.string,
  isUpdate: PropTypes.bool,
  readonly: PropTypes.bool,
  sharingId: PropTypes.string.isRequired,
  onCancel: PropTypes.func,
  onNext: PropTypes.func.isRequired
};

